import { useEffect, useMemo } from 'react'
import MyDriveIcons from '@/icons/MyDriveIcons'
import {
    ListObjectWithPaginationResponse,
    ListObjectWithPaginationV2,
} from '@/modules/Drive/services'
import useAuthStore from '@/stores/useAuthStore'
import useDriveStore from '@/stores/useDriveStore'
import FileGrid from '../FileGrid'
import ViewTypeSwitch from '../ViewTypeSwitch'
import { t } from 'i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from 'vnetwork-ui'
import DownLoadIcons from '@/icons/DownLoadIcons'
import { DownloadMultipleFilesParams, ObjectType } from '@/modules/Drive/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useInfiniteQueryWithHandler } from '@/hooks/useInfiniteQueryWithHandler'
import { useSearchParams } from 'react-router-dom'

const MyDrive = () => {
    const employee = useAuthStore((state) => state.employee)
    const viewType = useDriveStore((state) => state.viewType)
    const setViewType = useDriveStore((state) => state.setViewType)
    const isSelectedAll = useDriveStore((state) => state.isSelectedAll)
    const downloadMultipleItems = useDriveStore(
        (state) => state.downloadMultipleItems
    )
    const isSelectedMode = useDriveStore((state) => state.isSelectedMode)
    const setIsSelectedAll = useDriveStore((state) => state.setIsSelectedAll)
    const setIsSelectedMode = useDriveStore((state) => state.setIsSelectedMode)
    const selectedItems = useDriveStore((state) => state.selectedItems)
    const clearSelectedItems = useDriveStore(
        (state) => state.clearSelectedItems
    )

    const [searchParams] = useSearchParams()

    useEffect(() => {
        return () => {
            setIsSelectedMode(false)
            setIsSelectedAll(false)
            clearSelectedItems()
        }
    }, [])

    const {
        data,
        isLoading,
        isError,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQueryWithHandler({
        queryKey: [
            QUERY_KEYS.MY_DRIVE,
            employee?.employee_uuid,
            searchParams.get('search'),
        ],
        queryFn: ({ pageParam = null as string | null }) =>
            ListObjectWithPaginationV2(
                employee?.employee_uuid,
                pageParam ?? '',
                searchParams.get('search') ?? ''
            ),
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage: ListObjectWithPaginationResponse) =>
            lastPage?.nextContinuationToken ?? null,
        enabled: !!employee?.employee_uuid,
    })

    const items = useMemo(
        () => ({
            folders:
                ((data as any)?.pages as any)?.flatMap(
                    (page: any) => page?.folders || []
                ) || [],
            files:
                ((data as any)?.pages as any)?.flatMap(
                    (page: any) => page?.files || []
                ) || [],
        }),
        [data]
    )

    const handleDownload = async () => {
        if (selectedItems.length === 0 || !downloadMultipleItems) return
        if (!employee?.employee_uuid) {
            console.error('Employee UUID is required')
            return
        }
        const fullPath = `pub/drives/${employee?.employee_uuid}`
        const payload: DownloadMultipleFilesParams = {
            items: selectedItems.map((item) => ({
                object_type: item.endsWith('/')
                    ? ObjectType.FOLDER
                    : ObjectType.FILE,
                object_key: item,
            })),
            prefix: fullPath,
            zipName: `download-${Date.now()}.zip`,
        }

        setIsSelectedMode(false)
        setIsSelectedAll(false)
        clearSelectedItems()

        downloadMultipleItems(payload)
    }

    const actionGroup = () => {
        return (
            <div className="flex gap-4 items-center">
                <div
                    className="p-2 rounded hover:bg-gray-100 transition cursor-pointer"
                    title="Tải xuống"
                    onClick={handleDownload}
                >
                    <DownLoadIcons className="text-[#626262] size-5" />
                </div>
            </div>
        )
    }

    const SelectButtonGroup = () => {
        if (!isSelectedMode)
            return (
                <div className="flex gap-4 items-center">
                    <Button
                        color="primary"
                        onClick={() => {
                            setIsSelectedMode(true)
                        }}
                        startIcon={
                            <Checkbox
                                className="data-[state=checked]:border-white data-[state=checked]:text-white"
                                checked={true}
                            />
                        }
                    >
                        <p className="typo small text-white">Chọn</p>
                    </Button>
                </div>
            )
        return (
            <div className="flex gap-4 items-center">
                {selectedItems.length > 0 && actionGroup()}
                <p> đã chọn {selectedItems.length} mục</p>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setIsSelectedAll(!isSelectedAll)
                    }}
                >
                    <p className="typo small">
                        {isSelectedAll ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                    </p>
                </Button>
                <Button
                    color="error"
                    onClick={() => {
                        setIsSelectedMode(false)
                        setIsSelectedAll(false)
                        clearSelectedItems()
                    }}
                >
                    <p className="typo small">Hủy</p>
                </Button>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <MyDriveIcons className="w-5 h-5" />
                        <span>{t('my-drive-title')}</span>
                    </div>
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('uploading')}</div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <MyDriveIcons className="w-5 h-5" />
                        <span>{t('my-drive-title')}</span>
                    </div>
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-red-500">
                        {t('drive.error-loading-files')}:{' '}
                        {error?.message || t('something-went-wrong')}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <header className="flex items-center justify-between">
                <div className="font-semibold text-primary text-base flex items-center gap-3">
                    <MyDriveIcons className="w-5 h-5" />
                    <span>My Drive</span>
                </div>
                <div className="flex gap-4 items-center">
                    <SelectButtonGroup />
                    <ViewTypeSwitch value={viewType} onChange={setViewType} />
                </div>
            </header>

            {items.folders.length === 0 && items.files.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('empty-folder')}</div>
                </div>
            ) : (
                <div>
                    <FileGrid folders={items.folders} files={items.files} />
                </div>
            )}
            {hasNextPage && (
                <div className="flex justify-center py-4">
                    <button
                        className="px-4 py-2 bg-primary text-white rounded disabled:opacity-50"
                        onClick={() => {
                            fetchNextPage()
                        }}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? t('loading') : t('load-more')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default MyDrive
